import React from 'react'
import { getViewInSourceLabel } from 'src/components/Reusable/Evidence/sourceLabel'
import { ServiceDiscovery } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridValidRowModel,
} from '@mui/x-data-grid'
import {
  Evidence,
  Struct,
  Value,
} from '@trustero/trustero-api-web/lib/receptor_v1/receptor_pb'
import { InfoButton } from 'src/components/PageLayout/InfoButton'
import * as XLSX from 'xlsx'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { WORKFLOW_RECEPTOR_MODEL_IDS } from 'src/pages/Receptors/receptors.constants'
import { FlexRow } from 'src/components/Reusable/Flex'
import {
  DetailsCell,
  isDetailsValue,
  TextCell,
  ViewInSourceButton,
} from 'src/components/Reusable/Evidence/evidence.components'
import isObject from 'lodash/isObject'
import log from 'loglevel'
import Papa from 'papaparse'
import { formatTimestamp } from '../formatDate'
import { DATE_FORMATS } from '../dateConstants'
import { blankHeaderPlaceholder } from './evidence.constants'
import { ColumnHeaderTooltip, EvidenceTable } from './evidence.types'

const getHeaderKeys = (evidenceStruct: Struct): string[] => {
  const evidenceRows = evidenceStruct.getRowsList()
  const allHeaders: string[] = evidenceStruct.getColDisplayOrderList()
  if (evidenceRows.length === 0) {
    return []
  }

  return allHeaders
}

const toStringValue = (value: Value, useMarkdown?: boolean): string => {
  let stringValue = ''
  const valueType = value.getValueTypeCase()
  switch (valueType) {
    case Value.ValueTypeCase.DOUBLE_VALUE:
      stringValue = value.getDoubleValue().toString()
      return stringValue
    case Value.ValueTypeCase.FLOAT_VALUE:
      stringValue = value.getFloatValue().toString()
      return stringValue
    case Value.ValueTypeCase.INT32_VALUE:
      stringValue = value.getInt32Value().toString()
      return stringValue
    case Value.ValueTypeCase.INT64_VALUE:
      stringValue = value.getInt64Value().toString()
      return stringValue
    case Value.ValueTypeCase.UINT32_VALUE:
      stringValue = value.getUint32Value().toString()
      return stringValue
    case Value.ValueTypeCase.UINT64_VALUE:
      stringValue = value.getUint64Value().toString()
      return stringValue
    case Value.ValueTypeCase.BOOL_VALUE:
      if (value.getBoolValue()) {
        stringValue = useMarkdown ? ':heavy_check_mark:' : '\u2714'
      } else {
        stringValue = '-'
      }
      return stringValue
    case Value.ValueTypeCase.STRING_VALUE:
      stringValue = value.getStringValue().trim()
      return stringValue
    case Value.ValueTypeCase.TIMESTAMP_VALUE: {
      const timestamp = value.getTimestampValue()
      stringValue = timestamp
        ? formatTimestamp(timestamp, DATE_FORMATS.ISO)
        : ''
      return stringValue
    }
    case Value.ValueTypeCase.STRING_LIST_VALUE: {
      const stringList = value.getStringListValue()
      stringValue = stringList ? stringList.getValuesList().join(', ') : ''
      return stringValue
    }
    default:
      return stringValue
  }
}

export const getEvidenceRows = (evidenceStruct: Struct): GridRowsProp => {
  const headerKeys = getHeaderKeys(evidenceStruct)
  const rows = evidenceStruct.getRowsList()
  const gridRows: GridValidRowModel[] = []
  rows.forEach((row, index) => {
    const rowData: GridValidRowModel = {}
    rowData.id = index
    headerKeys.forEach((key, index) => {
      if (key.length === 0) {
        return
      }
      const value = row.getColsMap().get(key)
      rowData[`${headerKeys[index]}`] = value && toStringValue(value)
    })
    gridRows.push(rowData)
  })
  return gridRows
}

// A column renders as a hover table when its own values carry the
// {label,value}[] contract — decided from the evidence itself rather than from
// a list of known receptors or captions. Reads the first non-empty value in the
// column and stops there; the shape is uniform per column by construction.
const isDetailsColumn = (evidenceStruct: Struct, key: string): boolean => {
  const rows = evidenceStruct.getRowsList()
  for (const row of rows) {
    const value = row.getColsMap().get(key)
    const stringValue = value && toStringValue(value)
    if (stringValue) {
      return isDetailsValue(stringValue)
    }
  }
  return false
}

export const getEvidenceColumns = (evidenceStruct: Struct): GridColDef[] => {
  const headerKeys = getHeaderKeys(evidenceStruct)
  const columns: GridColDef[] = []
  headerKeys.forEach((key) => {
    if (key.length === 0) {
      return
    }
    columns.push({
      field: key,
      headerName: key,
      flex: 1,
      minWidth: 225,
      headerClassName: 'evidence-grid-column',
      cellClassName: 'evidence-grid-cell',
      align: 'center',
      headerAlign: 'center',
      renderCell: isDetailsColumn(evidenceStruct, key)
        ? (params: GridRenderCellParams) => (
            <DetailsCell value={params.value as string} />
          )
        : (params: GridRenderCellParams) => (
            <TextCell value={params.value as string} />
          ),
    })
  })
  return columns
}

export const getCsvRows = (csvString: string): GridValidRowModel[] => {
  const { data } = Papa.parse(csvString, {
    header: true, // Use the first row as column headers
    skipEmptyLines: true,
  })
  const isValidRowData = data.every((row) => isObject(row))
  if (!isValidRowData) {
    return []
  }
  const rows = (data as Record<string, string>[]).map(
    (row: Record<string, string>, index) => ({
      id: index,
      ...row,
    }),
  )
  return rows
}

export const getCsvColumns = (
  csvString: string,
  headerTooltip?: ColumnHeaderTooltip,
): GridColDef[] => {
  const { data } = Papa.parse(csvString, {
    header: true, // Use the first row as column headers
    skipEmptyLines: true,
  })
  const columns = Object.keys(data[0] || {})
  return columns.map((column) => {
    const renderHeaderFunc =
      headerTooltip && column === headerTooltip.header
        ? () => (
            <Tooltip
              id={`${headerTooltip.header}-column-tooltip`}
              tooltipBody={headerTooltip.tooltip}
              overlayType={TooltipOverlayType.tooltip}
              placement={TooltipPositions.top}
            >
              <FlexRow gap={8}>
                {column}
                <InfoButton />
              </FlexRow>
            </Tooltip>
          )
        : undefined
    return {
      field: column,
      headerName: column,
      flex: 1,
      minWidth: 225,
      headerClassName: 'csv-grid-column',
      cellClassName: 'csv-grid-cell',
      align: 'center',
      headerAlign: 'center',
      renderHeader: renderHeaderFunc,
    } as GridColDef
  })
}

const hasValue = (cell: unknown): boolean =>
  cell !== undefined && cell !== null && cell !== ''

// Row index → column index → how many columns the cell there spans.
type ExcelColSpans = Record<number, Record<number, number>>

// The row key that carries a row's column spans, by field.
const COL_SPANS = '__colSpans'

// Cells keep their raw values (strings, numbers, booleans); an empty cell is a
// hole in its row. A merge keeps its value in its top-left cell: every row it
// covers gets that value, so sorting and filtering see it on each row, and
// where it covers several columns their first cell spans them.
export const getExcelSheet = (
  sheet: XLSX.WorkSheet,
): { cells: unknown[][]; colSpans: ExcelColSpans } => {
  for (const addr in sheet) {
    if (addr.startsWith('!')) {
      continue // skip special keys
    }
    const cell = sheet[addr]
    if (cell.t === 'd') {
      // date
      // format as ISO string
      cell.v = (cell.v as Date).toISOString().split('T')[0]
      cell.t = 's'
      delete cell.w // delete the formatted text
    }
    sheet[addr] = cell
  }
  const cells = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1, // Read as an array of arrays (rows)
  })
  const colSpans: ExcelColSpans = {}
  // Merges are in sheet coordinates; cells start at the sheet's range.
  const origin = XLSX.utils.decode_range(sheet['!ref'] ?? 'A1').s
  ;(sheet['!merges'] ?? []).forEach(({ s, e }) => {
    const top = s.r - origin.r
    const col = s.c - origin.c
    const value = cells[top]?.[col]
    for (let r = top; r <= e.r - origin.r; r += 1) {
      if (!cells[r]) cells[r] = []
      cells[r][col] = value
      if (e.c > s.c) colSpans[r] = { ...colSpans[r], [col]: e.c - s.c + 1 }
    }
  })
  return { cells, colSpans }
}

// How many rows down the column names are looked for; title blocks run a
// handful of rows.
const MAX_HEADER_SCAN_ROWS = 15

// The index of the row holding the column names, or -1 for an empty sheet.
// Compliance workbooks often put a title block above the table (the company,
// the register's name, a note), so the first row with a value is not always
// the header. The header is the first row, within the first few, with
// enough filled cells (three, or two on a two-column sheet), mostly text and
// not one repeated stub; title rows fill one cell or repeat a value, data
// rows carry numbers and dates. Sheets with
// no such row (forms, notes, narrow tables) fall back to the first row with a
// value, as before. A note row that fills fewer than half as many cells as
// the fullest row is not the header either. Same rule as detect_header_row
// in hs-indexer, which decides the same thing for indexing.
export const findExcelHeaderRow = (sheet: unknown[][]): number => {
  const firstRowWithValues = sheet.findIndex((row) => row.some(hasValue))
  const scanned = sheet.slice(0, MAX_HEADER_SCAN_ROWS)
  const widest = scanned.reduce(
    (max, row) => Math.max(max, row.filter(hasValue).length),
    0,
  )
  const needed = Math.max(2, Math.min(3, widest), Math.ceil(widest / 2))
  for (let i = 0; i < scanned.length; i++) {
    const filled = scanned[i].filter(hasValue)
    if (filled.length < needed) continue
    const text = filled.filter((cell) => typeof cell === 'string')
    if (text.length * 2 < filled.length) continue
    // One repeated stub ("Select" across the row) is not a header; a header
    // may still repeat a column name.
    const distinct = new Set(filled.map((cell) => String(cell).trim()))
    if (distinct.size * 2 < filled.length) continue
    return i
  }
  return firstRowWithValues
}

// The filled cells of each row above the header, one line per filled row.
export const getExcelPreamble = (
  sheet: unknown[][],
  headerIndex: number,
): string[] =>
  sheet
    .slice(0, Math.max(0, headerIndex))
    .map((row) =>
      row
        .filter(hasValue)
        .map((cell) => String(cell).trim())
        .join(' '),
    )
    .filter((line) => line.length > 0)

export const getExcelColumns = (sheet: unknown[][]): GridColDef[] => {
  const firstRowWithValues = sheet[findExcelHeaderRow(sheet)]
  if (!firstRowWithValues) {
    return []
  }
  // Each column needs its own field, and `id` is the row's key: a repeated
  // header gets a suffixed field but keeps its header text.
  const usedFields = new Set(['id', COL_SPANS])
  // As wide as the widest row, so no cell is dropped when the header row is
  // shorter, e.g. a title alone above the real headers. Rows end at their last
  // value, and the empty cells within them are holes.
  const width = sheet.reduce((max, row) => Math.max(max, row.length), 0)
  return Array.from({ length: width }, (_, index) => {
    const col = String(firstRowWithValues[index] ?? '').trim()
    const baseField = col.length > 0 ? col : index.toString() // Use index as field name
    let field = baseField
    for (let n = 2; usedFields.has(field); n += 1) {
      field = `${baseField} (${n})`
    }
    usedFields.add(field)
    return {
      field,
      headerName: col.length > 0 ? col : `Column ${index + 1}`, // Handle empty headers
      flex: 1,
      minWidth: 225,
      headerClassName: 'csv-grid-column',
      cellClassName: 'csv-grid-cell',
      align: 'center',
      headerAlign: 'center',
    }
  }) as unknown as GridColDef[]
}

// The rows below the header row.
export const getExcelRows = (
  sheet: unknown[][],
  headers: GridColDef[],
  colSpans: ExcelColSpans = {},
): GridValidRowModel[] => {
  const headerIndex = findExcelHeaderRow(sheet)
  const rows: GridValidRowModel[] = []
  sheet.forEach((row, index) => {
    if (index <= headerIndex || !row.some(hasValue)) return
    const rowData = { id: rows.length + 1 } as GridValidRowModel // Each row must have a unique ID
    headers.forEach((col, colIndex) => {
      rowData[col.field] = row[colIndex] ?? '' // Handle empty cells
    })
    const spans = colSpans[index]
    if (spans) {
      rowData[COL_SPANS] = Object.fromEntries(
        Object.entries(spans).map(([colIndex, span]) => [
          headers[Number(colIndex)].field,
          span,
        ]),
      )
    }
    rows.push(rowData)
  })
  return rows
}

export const getEvidenceCsv = (
  body: string | Uint8Array,
  headerTooltip?: ColumnHeaderTooltip,
): { rows: GridValidRowModel[]; columns: GridColDef[] } => {
  const bodyString =
    typeof body === 'string' ? body : new TextDecoder().decode(body)
  const rows = getCsvRows(bodyString)
  const columns = getCsvColumns(bodyString, headerTooltip)
  return { rows, columns }
}

export type EvidenceExcelSheet = {
  name: string
  hidden: boolean
  // Null when the sheet can't be read into a grid, e.g. a date out of range.
  grid: {
    rows: GridValidRowModel[]
    columns: GridColDef[]
    // The title rows above the column names, one line each.
    preamble: string[]
  } | null
}

const getExcelGrid = (sheet: XLSX.WorkSheet): EvidenceExcelSheet['grid'] => {
  try {
    const { cells, colSpans } = getExcelSheet(sheet)
    if (cells.length === 0) return { rows: [], columns: [], preamble: [] }
    const columns = getExcelColumns(cells)
    const rows = getExcelRows(cells, columns, colSpans)
    const preamble = getExcelPreamble(cells, findExcelHeaderRow(cells))
    // Only columns a merge starts in span; a span travels with its row.
    const spanned = new Set(
      rows.flatMap((row) => Object.keys(row[COL_SPANS] ?? {})),
    )
    columns.forEach((column, index) => {
      if (!spanned.has(column.field)) return
      // The grid spans visible columns, so a span counts only its merge's
      // visible columns; a hidden one would otherwise cover the next.
      column.colSpan = (_value, row, _column, apiRef) => {
        const span = row[COL_SPANS]?.[column.field]
        if (!span) return undefined
        const merged = new Set(
          columns.slice(index, index + span).map(({ field }) => field),
        )
        return apiRef.current
          .getVisibleColumns()
          .filter(({ field }) => merged.has(field)).length
      }
    })
    return { rows, columns, preamble }
  } catch (error) {
    log.error('Error getting evidence excel sheet', error)
    return null
  }
}

// NOTE: This should be called in a try/catch block,
// because it throws an error if the file is not a readable workbook
export const getEvidenceExcel = (body: Uint8Array): EvidenceExcelSheet[] => {
  const workbook = XLSX.read(body, { type: 'array', cellDates: true })
  return workbook.SheetNames.map((name, index) => ({
    name,
    // Workbook.Sheets follows SheetNames' order; Hidden is 0, 1 or 2 (very hidden).
    hidden: !!workbook.Workbook?.Sheets?.[index]?.Hidden,
    grid: getExcelGrid(workbook.Sheets[name]),
  }))
}

/*
This function converts a receptor_v1.Struct to an ordered and displayable array header strings, and an array of rows of strings. Each row's columns are ordered according to its headers in the headers array.
*/
export const tabulate = (evidenceStruct: Struct): EvidenceTable => {
  const headerKeys = getHeaderKeys(evidenceStruct)
  const rows = evidenceStruct.getRowsList()
  const table: EvidenceTable = {
    headers: [],
    body: [],
    problemRows: [],
  }

  rows.forEach((row) => {
    const cols = Array(headerKeys.length).fill('')
    headerKeys.forEach((key, index) => {
      if (key.length === 0) {
        return
      }
      const value = row.getColsMap().get(key)
      cols[index] = value && toStringValue(value, true)
    })
    table.body.push(cols)
  })

  // Get displayable headers
  headerKeys.forEach((key) => {
    let name = key
    const colDisplayNames = evidenceStruct.getColDisplayNamesMap()
    const colDisplayName = colDisplayNames.get(key)
    if (colDisplayName) {
      name = colDisplayName
    }
    table.headers.push(name)
  })
  return table
}

export const getEvidenceSources = (evidence: Evidence): ServiceDiscovery[] => {
  const sources = evidence.getSourcesList()
  const discoveries: ServiceDiscovery[] = []
  sources.forEach((source) => {
    discoveries.push(
      new ServiceDiscovery()
        .setServiceId(evidence.getServiceName())
        .setApiCallsList([source.getRawApiRequest()])
        .setDiscovery(source.getRawApiResponse()),
    )
  })
  return discoveries
}

export const evidenceToMarkdown = (table: EvidenceTable): string => {
  if (table.headers.length === 0) {
    return ''
  }
  const preparedTable = prepareTable(table)
  const isColumnCenterJustified: boolean[] = Array(
    preparedTable.headers.length,
  ).fill(true)
  const parts = [
    printRow(preparedTable.headers),
    genColumnJustification(isColumnCenterJustified),
    printBody(preparedTable.body),
  ]
  return parts.join('\n')
}

const prepareTable = (table: EvidenceTable): EvidenceTable => {
  const annotatedTable = getAnnotatedTable(table)
  const preparedTable = excludeIgnored(annotatedTable)
  return preparedTable
}

const printRow = (row: string[]): string => {
  return `| ${row.join(' | ')} |`
}

const genColumnJustification = (isCenterJustified: boolean[]): string => {
  if (isCenterJustified.length === 0) {
    return ''
  }
  const columnJustification: string[] = []
  isCenterJustified.forEach((isCentered) => {
    if (isCentered) {
      columnJustification.push(':---:')
      return
    }
    columnJustification.push(':---')
  })
  return `|${columnJustification.join('|')}|`
}

const printBody = (body: string[][]): string => {
  if (body.length === 0) {
    return ''
  }
  const bodyMarkdown: string[] = Array(body.length)
  body.forEach((row, index) => {
    bodyMarkdown[index] = printRow(row)
  })
  return bodyMarkdown.join('\n')
}

const getAnnotatedTable = (table: EvidenceTable): EvidenceTable => {
  let hasProblem = false
  for (const problem of Object.values(table.problemRows)) {
    if (problem) {
      hasProblem = true
      break
    }
  }
  if (!hasProblem) {
    return table
  }

  if (table.headers.length !== 0) {
    table.headers.unshift(blankHeaderPlaceholder)
  }

  table.body.forEach((row, index) => {
    if (table.problemRows[index]) {
      row.forEach((content, contentIndex) => {
        row[contentIndex] = `***${content}***`
      })
      row.unshift('❌')
    } else {
      row.unshift('')
    }
    table.body[index] = row
  })
  return table
}

const excludeIgnored = (table: EvidenceTable): EvidenceTable => {
  const ignoredIndexes: Record<number, boolean> = {}
  let j = 0
  table.headers.forEach((header, index) => {
    if (header === '') {
      ignoredIndexes[index] = true
      return
    } else if (header === blankHeaderPlaceholder) {
      table.headers[j] = ''
    } else {
      table.headers[j] = header
    }
    j++
  })
  table.headers = table.headers.slice(0, j)

  table.body.forEach((row, index) => {
    let j = 0
    row.forEach((content, contentIndex) => {
      if (ignoredIndexes[contentIndex]) {
        return
      }
      row[j] = content
      j++
    })
    table.body[index] = row.slice(0, j)
  })
  return table
}

const workflowShortName = (receptorModelId: string): string | undefined => {
  if (receptorModelId === 'trr-jira-workflow') {
    return 'Jira'
  } else if (receptorModelId === 'trr-servicenow-workflow') {
    return 'ServiceNow'
  }
  return undefined
}

export const getEvidenceSource = (
  isAutomated: boolean,
  isLinkedDocument: boolean,
  receptorModelId?: string,
  docImportLink?: string,
  mime?: string,
): JSX.Element | string => {
  if (mime?.startsWith('tr-artifact/')) {
    return 'Artifact'
  }
  if (WORKFLOW_RECEPTOR_MODEL_IDS.includes(receptorModelId || '')) {
    if (receptorModelId && docImportLink) {
      const shortName = workflowShortName(receptorModelId)

      return (
        <ViewInSourceButton
          receptorModelId={receptorModelId}
          link={docImportLink}
          shortName={shortName}
          style={{ textAlign: 'left' }}
        />
      )
    }
  }

  if (isLinkedDocument) {
    if (receptorModelId && docImportLink) {
      return (
        <ViewInSourceButton
          receptorModelId={receptorModelId}
          link={docImportLink}
          style={{ textAlign: 'left' }}
        />
      )
    }
    return 'Linked Document'
  }
  if (isAutomated) {
    return 'Receptor'
  }
  return 'Upload'
}

// getEvidenceSource as text: the label its "View in ..." link shows, for search.
export const getEvidenceSourceLabel = (
  isAutomated: boolean,
  isLinkedDocument: boolean,
  receptorModelId?: string,
  docImportLink?: string,
  mime?: string,
): string => {
  const source = getEvidenceSource(
    isAutomated,
    isLinkedDocument,
    receptorModelId,
    docImportLink,
    mime,
  )
  if (typeof source === 'string') {
    return source
  }
  const modelId = receptorModelId || ''
  return getViewInSourceLabel(modelId, workflowShortName(modelId))
}
